import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { usePageLoader } from '@/components/LogoLoader'
import { api } from '@/api/client'
import { BarChart } from '@/charts/BarChart'
import { Meter } from '@/components/Meter'
import { StatTile } from '@/components/StatTile'
import { SERIES, formatValue } from '@/charts/palette'
import { tidyLabel } from '@/features/aufmass/types'
import { fmtDate, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { projectsMessages } from '@/i18n/messages/projects'
import { ProjectMaterial } from './ProjectMaterial'
import { ExecutionPanel } from '@/features/execution/ExecutionPanel'
import { WorkflowPanel } from '@/features/workflow/WorkflowPanel'
import { RegistersPanel } from '@/features/registers/RegistersPanel'

interface Header {
  projectId: string; name: string | null; code: string | null; codeInternal: string | null
  providerName: string | null; rom: string | null; status: number | null
  country: string | null; calcType: number | null
  contractValue: number | null; projectValue: number | null
  planedMetres: number | null; planedHp: number | null
  tbStart: string | null; tbEnd: string | null
  meterPrice: number | null; hpPrice: number | null; hasPrice: number | null
}
interface Executed {
  crewDays: number; workingDays: number; metres: number
  revenueSub: number; revenueMih: number; manHours: number; crews: number
  firstWorked: string | null; lastWorked: string | null
}
interface WeekPoint { isoWeek: number; label: string; metres: number; revenueSub: number; manHours: number }
interface NamedValue { name: string; value: number; secondary: number }
interface Detail {
  header: Header; executed: Executed
  weekly: WeekPoint[]; crews: NamedValue[]; positions: NamedValue[]
}

export function ProjectDetailPage() {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const { id } = useParams<{ id: string }>()
  const { data, isPending, error } = useQuery<Detail>({
    queryKey: ['project', id],
    queryFn: () => api.get<Detail>(`/api/projects/${id}`),
    enabled: Boolean(id),
  })

  const loader = usePageLoader(isPending, t('loadingProject'))
  if (loader) return loader
  if (!data && !error) return null
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('projectNotFound')}</p>

  const { header: h, executed: e } = data
  const calcLabel = h.calcType != null && CALC_KEYS[h.calcType] ? t(CALC_KEYS[h.calcType]!) : null

  return (
    <div className="space-y-6">
      <header>
        <Link to="/projects" className="text-xs text-neutral-500 hover:underline">← {tc('navProjects')}</Link>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">{h.name ?? t('unnamedProject')}</h1>
        <p className="mt-1 flex flex-wrap gap-x-4 text-sm text-neutral-500">
          {h.codeInternal && <span>{t('codeInternalShort', { code: h.codeInternal })}</span>}
          {h.code && <span>{t('codeExternalShort', { code: h.code })}</span>}
          {h.providerName && <span>{h.providerName}</span>}
          {h.rom && <span>ROM {h.rom}</span>}
          {h.country && <span>{h.country}</span>}
        </p>
      </header>

      {id && <WorkflowPanel projectId={id} />}
      {id && <RegistersPanel projectId={id} />}
      {id && <ExecutionPanel projectId={id} />}

      {/*
        Meters, not donuts: a single ratio against a limit. Where AU has no plan recorded
        the meter says so rather than drawing an empty bar — TbCompleted, PriceBISMeter and
        HasPlaned are populated on 0 of 109 active projects, so this is the common case.
      */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Meter label={t('executedMetres')} value={e.metres} limit={h.planedMetres} unit="m" color={SERIES.primary} />
        <StatTile label={t('subRevenue')} value={e.revenueSub} unit="EUR" />
        <StatTile label={t('crewDays')} value={e.crewDays} unit="" />
        <StatTile label={t('manHours')} value={e.manHours} unit="h" />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <Card title={t('contract')}>
          <Row label={t('contractValue')} value={h.contractValue} unit="EUR" />
          <Row label={t('projectValue')} value={h.projectValue} unit="EUR" />
          <Row label={t('plannedMetres')} value={h.planedMetres} unit="m" />
          <Row label={t('plannedHp')} value={h.planedHp} unit="" />
        </Card>

        <Card title={t('unitPricing')}>
          {/* From project_factoring: projects_new.PriceBISMeter is populated on 0 of 109. */}
          <Row label={t('perMetre')} value={h.meterPrice} unit="EUR" />
          <Row label={t('perHp')} value={h.hpPrice} unit="EUR" />
          <Row label={t('perHas')} value={h.hasPrice} unit="EUR" />
          <Row label={t('calcType')} text={calcLabel} />
        </Card>

        <Card title={t('activity')}>
          <Row label={t('firstWorked')} text={e.firstWorked ? fmtDate(e.firstWorked) : null} />
          <Row label={t('lastWorked')} text={e.lastWorked ? fmtDate(e.lastWorked) : null} />
          <Row label={t('workingDays')} value={e.workingDays} unit="" />
          <Row label={t('crewsInvolved')} value={e.crews} unit="" />
          <Row label={t('tbWindow')} text={h.tbStart || h.tbEnd ? `${h.tbStart ? fmtDate(h.tbStart) : '?'} → ${h.tbEnd ? fmtDate(h.tbEnd) : '?'}` : null} />
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <BarChart
          title={t('executedByWeek')}
          bars={data.weekly.map((w) => ({ label: w.label, value: w.metres }))}
          unit="m" color={SERIES.primary}
        />
        <BarChart
          title={t('subByWeek')}
          bars={data.weekly.map((w) => ({ label: w.label, value: w.revenueSub }))}
          unit="EUR" color={SERIES.money}
        />
      </section>

      <section className="grid items-start gap-4 lg:grid-cols-2">
        <BarChart
          title={t('whatWasBuilt')}
          subtitle={t('quantityByPosition')}
          bars={data.positions.map((p) => ({
            label: tidyLabel(p.name), value: p.value, note: tc('crewDays', { count: p.secondary }),
          }))}
          unit="m" color={SERIES.primary} orientation="bar"
        />
        <BarChart
          title={t('crewsOnProject')}
          bars={data.crews.map((c) => ({
            label: c.name, value: c.value, note: tc('crewDays', { count: c.secondary }),
          }))}
          unit="m" color={SERIES.primary} orientation="bar"
        />
      </section>

      {id && <ProjectMaterial projectId={id} />}
    </div>
  )
}

/** projects_new.CalcType — the billing model. Comment at ProjectsNew.cs:51. */
const CALC_KEYS: Record<number, 'calc0' | 'calc1' | 'calc2' | 'calc3' | 'calc4'> = {
  0: 'calc0', 1: 'calc1', 2: 'calc2', 3: 'calc3', 4: 'calc4',
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h3 className="text-sm font-semibold">{title}</h3>
      <dl className="mt-3 space-y-2 text-sm">{children}</dl>
    </div>
  )
}

/**
 * A field that is honest about absence.
 *
 * AU stores "not set" as 0 for most numerics, so the query nulls those out and this renders
 * "not recorded" — a zero shown as a figure is indistinguishable from a real zero, and on
 * this data most of them are not real.
 */
function Row({ label, value, unit, text }: {
  label: string; value?: number | null; unit?: string; text?: string | null
}) {
  const t = useT(projectsMessages)
  const shown = text ?? (value != null && unit != null ? formatValue(value, unit) : null)
  return (
    <div className="flex items-center justify-between gap-4 border-b border-neutral-100 pb-2 last:border-0 dark:border-neutral-800/60">
      <dt className="text-neutral-600 dark:text-neutral-400">{label}</dt>
      <dd className={shown ? 'tabular font-medium' : 'text-xs text-neutral-400'}>
        {shown ?? t('notRecorded')}
      </dd>
    </div>
  )
}
